<template>
  <data-table
    :service-provider="serviceProvider"
    :vuex-module="vuexModule"
    :header-columns="headerColumns"
    :query-params="queryParams"
    :actions-width="actionsWidth"
    :sort-by="sortBy"
    :sort-order="sortOrder"
    label="Reports"
    ref="datatable"
  >
    <template #header-bulk-delete>
      <div class="sr-only"></div>
    </template>
    <template #header-left v-if="!!rowsSelected.length">
      <b-button
        @click.prevent.stop="manageAccess(null)"
        variant="link"
        size="sm"
        class="text-info bg-transparent py-1 px-0 font-weight-normal d-flex align-items-center shadow-none"
        >Assign Access</b-button
      >
      <b-button
        v-if="$user.get().is_superuser"
        @click.stop="() => $emit('reports:assignTags', rowsSelected)"
        variant="link"
        size="sm"
        class="text-info bg-transparent py-1 px-0 font-weight-normal d-flex align-items-center shadow-none"
        >Assign Tags</b-button
      >
    </template>
    <template #header-right>
      <div class="navbar-search form-inline ml-auto">
        <b-form-group class="mb-0">
          <b-input-group class="input-group-alternative input-group-merge">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="fas fa-search"></i></span>
            </div>
            <b-form-input @input="onSearch" placeholder="Search" size="sm" type="text">
            </b-form-input>
          </b-input-group>
        </b-form-group>
      </div>
    </template>

    <template v-slot:cell_name_OR_display_name="{ value, row }">
      {{ row.display_name || value }}
    </template>
    <template v-slot:cell_display_name="{ value }">
      <template v-if="value">{{ value }}</template>
      <b-icon v-else icon="pencil" class="text-primary"></b-icon>
    </template>
    <template v-slot:cell_view_as="{ row, index, total }">
      <DropdownSearchable
        v-if="0 < reportAssignedCompanyOptions(row).length"
        @select="onCompanyDropdownSelect($event, row.id)"
        :options="reportAssignedCompanyOptions(row)"
        label="Company"
        class="company-dropdown"
        :dropdown-position="index == total - 1 ? 'top' : 'bottom'"
      />
    </template>
    <template v-slot:cell_tags="{ value, row }">
      <a
        href
        @click.prevent="
          $bus.$emit('toggle-modal', {
            key: 'report-tags',
            active: true,
            id: row.id,
          })
        "
      >
        <template v-if="!!value.length">
          <b-badge
            v-for="tag in value"
            :key="tag.id"
            variant="light"
            class="text-primary py-1 px-2 mr-2 border border-primary d-inline-flex align-items-center shadow"
            >{{ tag.name }}</b-badge
          >
        </template>
        <b-icon v-else icon="pencil" class="text-primary"></b-icon>
      </a>
    </template>
    <template v-slot:row-actions="row">
      <base-button @click.prevent.stop="manageAccess(row)" type="primary" outline
        >Manage Access</base-button
      >
    </template>
  </data-table>
</template>

<script>
import _ from "lodash";
import { reportsTable as data } from "@datas";
import { reportMixin } from "@mixins";

import DataTable from "@components/app/DataTable";
import DropdownSearchable from "@components/DropdownSearchable";

export default {
  name: "reports-table",
  components: {
    [DataTable.name]: DataTable,
    DropdownSearchable,
  },
  inject: ["reportSvc"],
  mixins: [reportMixin],
  data() {
    return {
      ...data,
    };
  },
  methods: {
    onSearch: _.debounce(function (keyword) {
      this.$bus.$emit("datatable-search", keyword);
    }, 500),
    manageAccess(row) {
      this.reportSvc.set("report", row);

      this.$bus.$emit("toggle-modal", {
        key: "report-access",
        active: true,
      });

      if (row && "undefined" !== typeof row.id) {
        this.$bus.$emit("datatable-selection-changed", [row]);
        this.$bus.$emit("datatable-unselect-rows");
      }
    },
    reportAssignedCompanyOptions(row) {
      // LIQ-2339: "View As" (company impersonation) has no meaning for a native report -
      // RecProductionReport always renders the real logged-in user's own company/role, not an
      // impersonated one, so this suppresses the dropdown entirely for native rows.
      if (row.is_native) return [];

      return row.company_assigned.map((item) => {
        return {
          value: item.company.id,
          text: item.company.name,
        };
      });
    },
    onCompanyDropdownSelect(companyId, reportId) {
      const encodedID = this.encodeReportID(reportId, companyId);

      this.$router.push({
        name: "ReportView",
        params: { id: encodedID },
      });
    },
  },
  created() {
    this.serviceProvider = this.reportSvc;
    this.queryParams.refresh = false;

    if ("Superuser" === this.$user.get().role) {
      this.queryParams.refresh = true;
      this.headerColumns = [
        {
          prop: "name",
          keys: ["name"],
          label: "Report Name",
          minWidth: 250,
          searchable: true,
          sortable: true,
          sortBy: "name",
        },
        {
          prop: "display_name",
          keys: ["display_name"],
          label: "Display Name",
          minWidth: 250,
          searchable: true,
          sortable: true,
          sortBy: "display_name",
        },
        {
          prop: "tags",
          keys: ["tags.name"],
          label: "Tags",
          minWidth: 250,
          searchable: true,
          sortable: true,
          sortBy: "tags",
        },
        {
          prop: "view_as",
          label: "View As",
          width: 300,
          minWidth: 120,
          searchable: false,
          sortable: false,
          class: "view-as",
        },
      ];
      this.sortBy = "name";
    } else if ("Company" == this.$user.get().type) {
      this.queryParams.company_id = this.$user.get().company
        ? this.$user.get().company.id
        : null;
    }
  },
  mounted() {
    this.$bus.trackEvent("datatable-cell-click", (row, column) => {
      this.reportSvc.set("reports", row);

      const encodedID = this.encodeReportID(row.report_id || row.id);

      if (0 <= ["name", "name_OR_display_name"].indexOf(column.property))
        this.$router.push({
          name: "ReportView",
          params: {
            id: encodedID,
          },
        });
      else if ("display_name" === column.property)
        this.$bus.$emit("toggle-modal", {
          key: "report-name",
          active: true,
        });
    });
    this.$bus.trackEvent("datatable-unselect-rows", () => {
      this.rowsSelected = [];
    });
    this.$bus.trackEvent(
      "datatable-selection-changed",
      (payload) => (this.rowsSelected = payload.map((item) => item.id))
    );
  },
  beforeDestroy() {
    this.$bus.destroyEvents();
  },
};
</script>

<style lang="scss">
.view-as {
  .cell {
    overflow: visible;
  }

  .company-dropdown {
    margin-bottom: 0;
    display: block;

    .form {
      &-control {
        height: 40px;
        padding: 5px 10px;
      }

      &-group {
        margin-bottom: 0;

        legend {
          display: none !important;
        }
      }
    }
  }
}
</style>
